/**
 * Fetch on navigation, paint from cache first.
 *
 * Nothing polls. Data loads when a screen is opened and when the student asks for
 * it — the same rule the dashboard follows, for the same reason there: several of
 * these endpoints meter AI budget or write an access log, and a background
 * interval turns both into noise.
 *
 * `snapshot` opts a screen into the launch cache. With it, `data` is populated
 * from IndexedDB before the request goes out, so the first paint is content rather
 * than a skeleton, and `stale` says the content on screen has not been confirmed
 * yet.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useConnectivity } from './connectivity'
import { readSnapshot, writeSnapshot } from './cache'

export interface Resource<T> {
  data: T | null
  /** Always a real `Error` (an `ApiError` where the status matters), never a bare
   *  `unknown` — `{error && <ErrorState/>}` in a screen would otherwise widen the
   *  whole JSX expression to `unknown` and fail to typecheck. */
  error: Error | null
  loading: boolean
  /** Showing cached content that has not been confirmed against the server. */
  stale: boolean
  refresh: () => void
  /** Replaces the local copy without a round trip, for an optimistic write. */
  set: (updater: T | ((current: T) => T)) => void
}

export function useResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  options: { snapshot?: string; userId?: string | null; enabled?: boolean } = {},
): Resource<T> {
  const { snapshot, userId, enabled = true } = options

  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [stale, setStale] = useState(false)
  const [nonce, setNonce] = useState(0)

  const { online } = useConnectivity()
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  const set = useCallback((updater: T | ((current: T) => T)) => {
    setData((current) => {
      if (typeof updater !== 'function') return updater
      if (current === null) return current
      return (updater as (value: T) => T)(current)
    })
  }, [])

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }

    const controller = new AbortController()
    let cancelled = false

    void (async () => {
      // Paint the last known good copy first. Only on the initial load — a manual
      // refresh should not flash old content back onto the screen.
      if (snapshot && userId && nonce === 0) {
        const cached = await readSnapshot<T>(snapshot, userId)
        if (cached !== null && !cancelled) {
          setData(cached)
          setStale(true)
        }
      }

      setLoading(true)
      try {
        const result = await fetcherRef.current(controller.signal)
        if (cancelled || controller.signal.aborted) return
        setData(result)
        setError(null)
        setStale(false)
        if (snapshot && userId) void writeSnapshot(snapshot, userId, result)
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return
        // Offline with a cached copy is not an error state — the content on
        // screen is still the most recent thing we know, and the offline banner
        // is already saying so.
        if (!navigator.onLine && snapshot) setStale(true)
        else setError(caught instanceof Error ? caught : new Error(String(caught)))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled, snapshot, userId])

  // Coming back online revalidates whatever is on screen, once.
  useEffect(() => {
    if (online && stale) refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online])

  return { data, error, loading, stale, refresh, set }
}
